import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Icon,
  Input,
  ScrollArea,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Switch,
  Text,
} from "@salestrak/ui";

import {
  cellEditable,
  cellGrants,
  defaultInviteState,
  PERMISSION_REGISTRY,
  SYSTEM_ROLE_HIERARCHY,
  SYSTEM_ROLE_NAMES,
  type CellState,
  type PermissionDefinition,
  type SystemRole,
} from "@salestrak/permissions";
import { useCreateCustomRole } from "#app/lib/hooks/use-create-custom-role";
import { useInviteGrantMatrix } from "#app/lib/hooks/use-invite-grant-matrix";
import { graphQLErrorMessage } from "#app/lib/utils/toast-errors";

// "New role" dialog launched from the Custom Role Permissions section.
// Two-column matrix: a base-role dropdown that snaps the LEFT column
// to the picked role's defaults, and a NEW ROLE column the admin can
// adjust freely (except for cells the registry locks for the chosen
// base, which stay non-editable in both columns).
//
// Submit creates the role via `createCustomRole` (which seeds the
// template's defaults) and then writes per-cell overrides for anything
// the admin diverged from the base.

type NewRoleDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
};

const SECTION_LABELS: Record<string, string> = {
  manage: "PEOPLE",
  workflows: "WORKFLOWS",
  create_delete: "CREATE & DELETE",
  custom_fields: "CUSTOM FIELDS",
  comments: "COMMENTS",
  collaboration: "COLLABORATION",
};

// Invite targets shown in the INVITES section — these live in
// role_invite_grant rather than the permission registry, so the rows
// are hand-rolled. Order mirrors the design (Members → Guests →
// Limited Members).
const INVITE_TARGETS: ReadonlyArray<{
  target: SystemRole;
  label: string;
  description: string;
}> = [
  {
    target: "member",
    label: "Invite Members",
    description:
      "Invite Users to the Workspace, and share Workflows, Views, and Projects with other Users or Teams when the required access level is met.",
  },
  {
    target: "guest",
    label: "Invite Guests",
    description:
      "Invite Guests to the Workspace, and share Workflows, Views, and Projects with other Users or Teams when the required access level is met.",
  },
  {
    target: "limited_member",
    label: "Invite Limited Members",
    description:
      "Invite Limited Members to the Workspace, and share Workflows, Views, and Projects with other Users or Teams when the required access level is met.",
  },
];

// `workspace`-category keys (issue_types / reasons / trash manage) are
// deliberately absent — they're backend-only for now and shouldn't be
// editable from the role UI.
const SECTION_ORDER: readonly string[] = [
  "collaboration",
  "manage",
  "workflows",
  "create_delete",
  "custom_fields",
  "comments",
];

const BASE_ROLE_OPTIONS: ReadonlyArray<{
  value: SystemRole;
  label: string;
}> = [
  { value: "guest", label: SYSTEM_ROLE_NAMES.guest },
  { value: "limited_member", label: SYSTEM_ROLE_NAMES.limited_member },
  { value: "member", label: SYSTEM_ROLE_NAMES.member },
  { value: "admin", label: SYSTEM_ROLE_NAMES.admin },
];

export function NewRoleDialog({
  open,
  onOpenChange,
  workspaceId,
}: NewRoleDialogProps) {
  const [roleName, setRoleName] = useState("");
  const [baseRole, setBaseRole] = useState<SystemRole>("member");
  // newCells: when omitted for a key, it tracks the base default.
  // When set, the admin has explicitly overridden the cell.
  const [newCells, setNewCells] = useState<Record<string, CellState>>({});
  // inviteCells: same override semantics for the INVITES rows, keyed
  // by target SystemRole. Omitted = tracks the base role's default.
  const [inviteCells, setInviteCells] = useState<
    Partial<Record<SystemRole, boolean>>
  >({});
  const [submitting, setSubmitting] = useState(false);
  // The server's refusal of the name (a built-in role's, or one taken).
  const [nameError, setNameError] = useState("");

  const create = useCreateCustomRole(workspaceId);
  // Role ids for the invite-grant writes on submit — the registry only
  // knows system-role KEYS; the grant mutation needs per-workspace ids.
  const { matrix: inviteMatrix } = useInviteGrantMatrix(workspaceId);

  // Reset overrides whenever the base role changes — per the spec,
  // changing the base snaps the new role column to match.
  useEffect(() => {
    setNewCells({});
    setInviteCells({});
  }, [baseRole]);

  // Reset everything on open/close.
  useEffect(() => {
    if (!open) {
      setRoleName("");
      setNameError("");
      setBaseRole("member");
      setNewCells({});
      setInviteCells({});
      setSubmitting(false);
    }
  }, [open]);

  const sections = useMemo(() => groupByCategory(PERMISSION_REGISTRY), []);
  // The new role's column names itself by the typed name once there is one.
  const newRoleLabel = roleName.trim() || "New role";

  // Invite cell resolution. Base column: the registry's default state
  // for (baseRole → target). New role column: custom roles inherit the
  // template's hierarchy, so the ceiling rule (can't invite above your
  // tier) locks targets that outrank the base. Editable cells default
  // to the base column's verdict.
  const inviteBaseState = (target: SystemRole): CellState =>
    defaultInviteState(
      { hierarchy: SYSTEM_ROLE_HIERARCHY[baseRole], systemRole: baseRole },
      { hierarchy: SYSTEM_ROLE_HIERARCHY[target], systemRole: target },
    );
  const inviteLocked = (target: SystemRole): boolean =>
    SYSTEM_ROLE_HIERARCHY[target] > SYSTEM_ROLE_HIERARCHY[baseRole];
  const inviteNewValue = (target: SystemRole): boolean =>
    inviteCells[target] ?? cellGrants(inviteBaseState(target));

  const getBaseState = (perm: PermissionDefinition): CellState =>
    perm.defaultByRole[baseRole];

  const getNewState = (perm: PermissionDefinition): CellState =>
    newCells[perm.key] ?? getBaseState(perm);

  const toggleNewCell = (perm: PermissionDefinition, on: boolean) => {
    const base = getBaseState(perm);
    if (!cellEditable(base)) return;
    setNewCells((prev) => {
      const next = { ...prev };
      next[perm.key] = on ? "yes" : "no";
      return next;
    });
  };

  // Only keys where the new column diverges from the base default
  // need a follow-up setRolePermission call after createCustomRole.
  const overrides = useMemo(() => {
    const out: Record<string, CellState> = {};
    for (const [key, state] of Object.entries(newCells)) {
      const def = PERMISSION_REGISTRY.find((p) => p.key === key);
      if (!def) continue;
      if (state !== def.defaultByRole[baseRole]) out[key] = state;
    }
    return out;
  }, [newCells, baseRole]);

  const handleCreate = async () => {
    if (!roleName.trim() || submitting) return;
    setSubmitting(true);
    try {
      // Custom roles seed `no` for every editable invite pair, so only
      // toggled-ON targets need a grant write. Resolve target role ids
      // from the workspace's invite matrix (system rows by name).
      const inviteGrants: Array<{ invitableRoleId: string; granted: boolean }> =
        [];
      for (const row of INVITE_TARGETS) {
        if (inviteLocked(row.target) || !inviteNewValue(row.target)) continue;
        const targetRole = (inviteMatrix?.invitableRoles ?? []).find(
          (r) =>
            r.isSystem && r.roleName === SYSTEM_ROLE_NAMES[row.target],
        );
        if (targetRole) {
          inviteGrants.push({ invitableRoleId: targetRole.id, granted: true });
        }
      }
      await create({
        roleName: roleName.trim(),
        template: baseRole,
        overrides,
        inviteGrants,
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to create custom role:", error);
      setNameError(graphQLErrorMessage(error) ?? "Failed to create role");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        size="4xl"
        dialogOptions={{
          primary: { label: "Create role", onClick: handleCreate, disabled: !roleName.trim() || submitting, loading: submitting },
          cancel: { disabled: submitting },
        }}
      >
        <DialogHeader>
          <DialogTitle>New role</DialogTitle>
          <DialogDescription>
            Start from an existing role and toggle the permissions that should
            differ.
          </DialogDescription>
        </DialogHeader>

        <DialogPanel>
          <Box gap="md">
            <Field invalid={!!nameError}>
              <FieldLabel>Role name</FieldLabel>
              <Input
                value={roleName}
                onChange={(e) => {
                  setRoleName(e.target.value);
                  setNameError("");
                }}
                placeholder="e.g. Sales Lead"
                autoFocus
              />
              {nameError ? <FieldError match>{nameError}</FieldError> : null}
            </Field>

            <Box border rounded="lg" overflow="hidden">
              {/* Header row */}
              <Box direction="row" surface="sunken" border="bottom">
                <Box fill="flex" paddingX="md" paddingY="sm" justify="center">
                  <Text size="xs" variant="muted" weight="semibold">
                    PERMISSION
                  </Text>
                </Box>
                <Box
                  width={160}
                  paddingX="sm"
                  paddingY="2xs"
                  border="left"
                  align="center"
                  justify="center"
                >
                  {/* Base UI's Select is value-based (no `{value,label}`
                      Option object). */}
                  <Select
                    value={baseRole}
                    onValueChange={(next: string | null) => {
                      if (!next) return;
                      setBaseRole(next as SystemRole);
                    }}
                  >
                    <SelectTrigger size="sm">
                      <SelectValue>
                        {(value: string) =>
                          SYSTEM_ROLE_NAMES[value as SystemRole]
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectPopup>
                      {BASE_ROLE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectPopup>
                  </Select>
                </Box>
                <Box
                  width={140}
                  paddingX="md"
                  paddingY="sm"
                  border="left"
                  align="center"
                  justify="center"
                >
                  {/* Live-tracks the role name input; falls back to the
                      generic label until the admin types one. */}
                  <Text
                    size="xs"
                    variant="muted"
                    weight="semibold"
                    truncate={1}
                  >
                    {roleName.trim() ? roleName.trim().toUpperCase() : "NEW ROLE"}
                  </Text>
                </Box>
              </Box>

              <Box direction="column" maxHeight={420}>
              <ScrollArea>
                {/* INVITES — sourced from role_invite_grant defaults
                    rather than the permission registry. Same two-column
                    treatment as the registry rows below. */}
                <Box border="bottom">
                  <Box direction="row" surface="sunken">
                    <Box fill="flex" paddingX="md" paddingY="xs">
                      <Text size="xs" variant="muted" weight="semibold">
                        INVITES
                      </Text>
                    </Box>
                    <Box width={160} border="left" />
                    <Box width={140} border="left" />
                  </Box>
                  {INVITE_TARGETS.map((row, ri) => {
                    const base = inviteBaseState(row.target);
                    const baseLocked = !cellEditable(base);
                    const locked = inviteLocked(row.target);
                    return (
                      <Box
                        key={`${row.target}-${ri}`}
                        direction="row"
                        hoverBackground
                      >
                        <Box fill="flex" paddingX="md" paddingY="sm" gap="2xs">
                          <Text size="sm" weight="semibold">
                            {row.label}
                          </Text>
                          <Text size="xs" variant="muted">
                            {row.description}
                          </Text>
                        </Box>
                        <Box
                          width={160}
                          paddingX="md"
                          paddingY="sm"
                          align="center"
                          justify="center"
                          border="left"
                        >
                          {baseLocked ? (
                            cellGrants(base) ? (
                              <Icon name="check" size="sm" color="muted" />
                            ) : (
                              <Icon name="x" size="sm" color="muted" />
                            )
                          ) : (
                            <Switch
                              aria-label={`${row.label}: ${SYSTEM_ROLE_NAMES[baseRole]}`}
                              checked={cellGrants(base)}
                              disabled
                              onCheckedChange={() => {}}
                            />
                          )}
                        </Box>
                        <Box
                          width={140}
                          paddingX="md"
                          paddingY="sm"
                          align="center"
                          justify="center"
                          border="left"
                        >
                          {locked ? (
                            <Icon name="x" size="sm" color="muted" />
                          ) : (
                            <Switch
                              aria-label={`${row.label}: ${newRoleLabel}`}
                              checked={inviteNewValue(row.target)}
                              onCheckedChange={(on) =>
                                setInviteCells((prev) => ({
                                  ...prev,
                                  [row.target]: on,
                                }))
                              }
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                {sections.map((section, si) => (
                  <Box
                    key={`${section.category}-${si}`}
                    border={si < sections.length - 1 ? "bottom" : undefined}
                  >
                    <Box direction="row" surface="sunken">
                      <Box fill="flex" paddingX="md" paddingY="xs">
                        <Text size="xs" variant="muted" weight="semibold">
                          {SECTION_LABELS[section.category] ??
                            section.category.toUpperCase()}
                        </Text>
                      </Box>
                      <Box width={160} border="left" />
                      <Box width={140} border="left" />
                    </Box>
                    {section.perms.map((perm, pi) => {
                      const base = getBaseState(perm);
                      const next = getNewState(perm);
                      const locked = !cellEditable(base);
                      return (
                        <Box
                          key={`${perm.key}-${pi}`}
                          direction="row"
                          hoverBackground
                        >
                          <Box fill="flex" paddingX="md" paddingY="sm" gap="2xs">
                            <Text size="sm" weight="semibold">
                              {perm.label}
                            </Text>
                            <Text size="xs" variant="muted">
                              {perm.description}
                            </Text>
                          </Box>
                          <Box
                            width={160}
                            paddingX="md"
                            paddingY="sm"
                            align="center"
                            justify="center"
                            border="left"
                          >
                            {locked ? (
                              cellGrants(base) ? (
                                <Icon name="check" size="sm" color="muted" />
                              ) : (
                                <Icon name="x" size="sm" color="muted" />
                              )
                            ) : (
                              <Switch
                                aria-label={`${perm.label}: ${SYSTEM_ROLE_NAMES[baseRole]}`}
                                checked={cellGrants(base)}
                                disabled
                                onCheckedChange={() => {}}
                              />
                            )}
                          </Box>
                          <Box
                            width={140}
                            paddingX="md"
                            paddingY="sm"
                            align="center"
                            justify="center"
                            border="left"
                          >
                            {locked ? (
                              cellGrants(base) ? (
                                <Icon name="check" size="sm" color="muted" />
                              ) : (
                                <Icon name="x" size="sm" color="muted" />
                              )
                            ) : (
                              <Switch
                                aria-label={`${perm.label}: ${newRoleLabel}`}
                                checked={cellGrants(next)}
                                onCheckedChange={(on) => toggleNewCell(perm, on)}
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </ScrollArea>
              </Box>
            </Box>
          </Box>
        </DialogPanel>

      </DialogPopup>
    </Dialog>
  );
}

function groupByCategory(
  perms: readonly PermissionDefinition[],
): Array<{ category: string; perms: PermissionDefinition[] }> {
  const grouped = new Map<string, PermissionDefinition[]>();
  for (const p of perms) {
    const arr = grouped.get(p.category) ?? [];
    arr.push(p);
    grouped.set(p.category, arr);
  }
  return SECTION_ORDER.filter((c) => grouped.has(c)).map((c) => ({
    category: c,
    perms: grouped.get(c)!,
  }));
}
